import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { tradeInApi } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { Recycle, ChevronRight, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export default function AccountTradeInPage() {
  const { tradeIns, loading, refreshData } = useOutletContext();
  const { lang, t, tField } = useLang();
  const { toast } = useToast();
  const [eligibleItems, setEligibleItems] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(true);

  useEffect(() => {
    fetchEligible();
  }, []);

  const fetchEligible = async () => {
    try {
      const response = await tradeInApi.getEligible();
      setEligibleItems(response.data.eligible_items || []);
    } catch (error) {
      console.error('Error fetching eligible items:', error);
    } finally {
      setLoadingEligible(false);
    }
  };

  const handleRequestTradeIn = async (item) => {
    try {
      await tradeInApi.request({
        order_item_id: item.order_id,
        product_id: item.product?.product_id,
        size: item.size,
        color: item.color,
        discount_percentage: item.discount_percentage,
        original_price: item.original_price,
        reason: 'Trade-in for discount',
      });
      toast({ title: 'Richiesta Biolosophy Value inviata!' });
      refreshData();
      fetchEligible();
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile inviare la richiesta',
        variant: 'destructive',
      });
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return t({ it: 'In attesa', en: 'Pending' });
      case 'received': return t({ it: 'Capo ricevuto — in verifica', en: 'Item received — under review' });
      case 'approved': return t({ it: 'Approvato', en: 'Approved' });
      case 'rejected': return t({ it: 'Rifiutato', en: 'Rejected' });
      case 'completed': return t({ it: 'Completato', en: 'Completed' });
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'bg-[#B8860B] text-white';
      case 'approved': return 'bg-[#4A5D23] text-white';
      case 'completed': return 'bg-green-600 text-white';
      case 'rejected': return 'bg-[#C05746] text-white';
      default: return 'bg-[#E5E0D8] text-[#57534E]';
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-light mb-2">Biolosophy Value</h2>
        <p className="text-[#57534E]">
          {t({
            it: "Restituisci i tuoi capi Biolosophy e ottieni sconti sul prossimo acquisto. Lo sconto si applica dopo l'approvazione del reso.",
            en: 'Bring back your Biolosophy garments and earn discounts on your next purchase. The discount is applied after the return has been approved.',
          })}
        </p>
      </div>

      {/* Approved discount banner */}
      {tradeIns.some((t) => t.status === 'approved' && !t.used) && (
        <div
          data-testid="approved-discount-banner"
          className="bg-[#4A5D23] text-white p-6 flex flex-wrap items-center justify-between gap-4"
        >
          <div>
            <p className="font-medium mb-1">{t({ it: 'Il tuo reso è stato approvato!', en: 'Your return has been approved!' })}</p>
            <p className="text-sm text-white/80">
              {t({ it: 'Hai uno sconto del', en: 'You have a' })}{' '}
              {Math.max(...tradeIns.filter((tr) => tr.status === 'approved' && !tr.used).map((tr) => tr.discount_percentage || 0))}%
              {t({ it: ' pronto: verrà applicato automaticamente al checkout.', en: ' discount ready: it will be applied automatically at checkout.' })}
            </p>
          </div>
          <Link to="/shop" className="bg-white text-[#1C1917] px-5 py-2.5 text-sm font-medium hover:bg-[#F2EFE9] transition-colors">
            {t({ it: 'Usa lo sconto', en: 'Use your discount' })}
          </Link>
        </div>
      )}

      {/* Eligible Items */}
      <div className="bg-[#F2EFE9] p-6">
        <h3 data-testid="armadio-heading" className="text-lg font-medium mb-4">
          {t({ it: 'Il Tuo Armadio Biolosophy Value', en: 'Your Biolosophy Value Wardrobe' })}
        </h3>
        {loadingEligible ? (
          <div className="animate-pulse space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-24 bg-[#E5E0D8]" />
            ))}
          </div>
        ) : eligibleItems.length === 0 ? (
          <div className="text-center py-8">
            <Recycle className="w-12 h-12 mx-auto text-[#E5E0D8] mb-3" />
            <p className="text-[#57534E] mb-4">
              {t({ it: 'Il tuo armadio è ancora vuoto.', en: 'Your wardrobe is still empty.' })}
              <br />{t({ it: 'Lo sconto del 20% parte già dal primo anno di acquisto.', en: 'The 20% discount starts from the very first year of purchase.' })}
            </p>
            <Link
              to="/shop"
              className="text-[#4A5D23] font-medium flex items-center justify-center gap-2 hover:underline"
            >
              Scopri la Collezione
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {eligibleItems.map((item, idx) => (
              <div
                key={idx}
                data-testid={`eligible-item-${idx}`}
                className="flex gap-4 p-4 bg-white"
              >
                <img
                  src={item.product?.images?.[0] || 'https://via.placeholder.com/80x100'}
                  alt={item.product?.name}
                  className="w-20 h-24 object-cover"
                />
                <div className="flex-1">
                  <h4 className="font-medium">{tField(item.product, 'name')}</h4>
                  <p className="text-sm text-[#57534E]">
                    {item.size} / {item.color}
                  </p>
                  <p className="text-sm text-[#57534E] mt-1">
                    {t({ it: 'Acquistato', en: 'Purchased' })}: {new Date(item.order_date).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT')}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-lg font-medium text-[#4A5D23]">
                      {item.discount_percentage}% {t({ it: 'di sconto', en: 'discount' })}
                    </span>
                    <span className="text-sm text-[#57534E]">
                      ({item.years_owned} {item.years_owned === 1 ? t({ it: 'anno', en: 'year' }) : t({ it: 'anni', en: 'years' })})
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleRequestTradeIn(item)}
                  data-testid={`request-trade-in-${idx}`}
                  className="self-center px-4 py-2 bg-[#4A5D23] text-white text-sm hover:bg-[#3B4A1C] transition-colors"
                >
                  {t({ it: 'Richiedi Biolosophy Value', en: 'Request Biolosophy Value' })}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trade-In History */}
      <div className="bg-[#F2EFE9] p-6">
        <h3 className="text-lg font-medium mb-4">{t({ it: 'Storico Biolosophy Value', en: 'Biolosophy Value History' })}</h3>
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 bg-[#E5E0D8]" />
            ))}
          </div>
        ) : tradeIns.length === 0 ? (
          <p className="text-[#57534E] text-center py-8">
            Nessuna richiesta Biolosophy Value effettuata
          </p>
        ) : (
          <div className="space-y-3">
            {tradeIns.map((tradeIn, idx) => (
              <div
                key={tradeIn.trade_in_id}
                data-testid={`trade-in-history-${idx}`}
                className="flex justify-between items-center gap-4 p-4 bg-white"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {tradeIn.product_image && (
                    <img src={tradeIn.product_image} alt="" className="w-12 h-16 object-cover flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {t({ it: tradeIn.product_name, en: tradeIn.product_name_en || tradeIn.product_name }) || tradeIn.trade_in_id}
                    </p>
                    <p className="text-sm text-[#57534E] mt-1">
                      {new Date(tradeIn.created_at).toLocaleDateString('it-IT')}
                    </p>
                    {tradeIn.status === 'rejected' && tradeIn.rejection_reason && (
                      <p className="text-xs text-[#C05746] mt-1" data-testid={`rejection-reason-${idx}`}>
                        {t({ it: 'Motivo: ', en: 'Reason: ' })}{tradeIn.rejection_reason}
                      </p>
                    )}
                  </div>
                </div>
                <span className={`px-3 py-1 text-sm flex-shrink-0 ${getStatusColor(tradeIn.status)}`}>
                  {getStatusLabel(tradeIn.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-[#1C1917] text-white p-6">
        <h3 className="text-lg font-medium mb-4">{t({ it: 'Come funziona?', en: 'How does it work?' })}</h3>
        <ol className="space-y-3 text-white/80">
          <li className="flex gap-3">
            <span className="font-medium text-[#4A5D23]">1.</span>
            {t({ it: 'Seleziona un capo idoneo dalla lista sopra', en: 'Select an eligible garment from the list above' })}
          </li>
          <li className="flex gap-3">
            <span className="font-medium text-[#4A5D23]">2.</span>
            {t({ it: 'Invia la richiesta di Biolosophy Value', en: 'Send your Biolosophy Value request' })}
          </li>
          <li className="flex gap-3">
            <span className="font-medium text-[#4A5D23]">3.</span>
            {t({ it: "Ricevi l'etichetta di reso via email", en: 'Receive the return label by email' })}
          </li>
          <li className="flex gap-3">
            <span className="font-medium text-[#4A5D23]">4.</span>
            {t({ it: 'Spedisci il capo e ricevi lo sconto', en: 'Ship the garment and receive your discount' })}
          </li>
        </ol>
        <Link
          to="/club"
          className="inline-flex items-center gap-2 mt-6 text-[#4A5D23] hover:underline"
        >
          {t({ it: 'Scopri di più sul programma', en: 'Learn more about the program' })}
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
